import type { FieldCalendarViewMode, FieldVisitFilter } from "./fieldCalendarTypes";
import {
  logFieldCalendarUrlCrash,
  runFieldCalendarUrlSafe,
} from "./fieldCalendarDiagnostics";
import {
  coerceValidDay,
  isValidDate,
  localDateInputValue,
  parseLocalDateInput,
  startOfDay,
  startOfMonth,
} from "./fieldCalendarUtils";

export const FIELD_CALENDAR_PATH = "/calendar/field";
export const FIELD_CALENDAR_FALLBACK_HREF = "/calendar/field";

const VIEWS = new Set<FieldCalendarViewMode>(["month", "week", "day"]);
const LANES = new Set<FieldVisitFilter>(["all", "service", "installation", "programming"]);
const MAX_RETURN_TO_LEN = 2048;

export type FieldCalendarUrlState = {
  view: FieldCalendarViewMode;
  selectedDay: Date;
  anchorDate: Date;
  mySchedule: boolean;
  laneFilter: FieldVisitFilter;
};

function safeSearchParams(searchStr: string): URLSearchParams {
  try {
    const raw =
      typeof searchStr === "string"
        ? searchStr.startsWith("?")
          ? searchStr.slice(1)
          : searchStr
        : "";
    return new URLSearchParams(raw);
  } catch (error) {
    logFieldCalendarUrlCrash("searchParams", error, { searchStr });
    return new URLSearchParams();
  }
}

function parseMonthParam(value: string | null): Date | null {
  if (!value || typeof value !== "string") return null;
  const m = value.trim().match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return null;
  }
  const d = startOfMonth(new Date(year, month - 1, 1, 12, 0, 0, 0));
  return isValidDate(d) ? coerceValidDay(d) : null;
}

function parseDateParam(value: string | null, fallback: Date): Date {
  if (!value || typeof value !== "string") return coerceValidDay(fallback);
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return coerceValidDay(fallback);
  const d = parseLocalDateInput(trimmed);
  return isValidDate(d) ? coerceValidDay(d) : coerceValidDay(fallback);
}

function parseViewParam(value: string | null): FieldCalendarViewMode {
  if (!value || typeof value !== "string") return "month";
  const v = value.trim().toLowerCase();
  return VIEWS.has(v as FieldCalendarViewMode) ? (v as FieldCalendarViewMode) : "month";
}

function parseMineParam(value: string | null): boolean {
  if (!value || typeof value !== "string") return false;
  const v = value.trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

function parseLaneParam(value: string | null, view: FieldCalendarViewMode): FieldVisitFilter {
  if (view !== "day") return "all";
  if (!value || typeof value !== "string") return "all";
  const lane = value.trim().toLowerCase();
  return LANES.has(lane as FieldVisitFilter) ? (lane as FieldVisitFilter) : "all";
}

/** Read URL search from wouter `useSearch()` (with or without leading `?`). */
export function parseFieldCalendarUrlSearch(searchStr: string): FieldCalendarUrlState {
  return runFieldCalendarUrlSafe(
    "parse",
    () => {
      const params = safeSearchParams(searchStr);
      const today = startOfDay(new Date());

      const selectedDay = parseDateParam(params.get("date"), today);
      const view = parseViewParam(params.get("view"));

      const monthAnchor = parseMonthParam(params.get("month"));
      let anchorDate: Date;
      if (monthAnchor) {
        anchorDate = monthAnchor;
      } else if (view === "month") {
        anchorDate = startOfMonth(selectedDay);
      } else {
        anchorDate = selectedDay;
      }

      return {
        view,
        selectedDay: coerceValidDay(selectedDay),
        anchorDate: coerceValidDay(anchorDate),
        mySchedule: parseMineParam(params.get("mine")),
        laneFilter: parseLaneParam(params.get("lane"), view),
      };
    },
    {
      view: "month",
      selectedDay: startOfDay(new Date()),
      anchorDate: startOfMonth(startOfDay(new Date())),
      mySchedule: false,
      laneFilter: "all",
    },
    { query: searchStr },
  );
}

export function buildFieldCalendarHref(state: FieldCalendarUrlState): string {
  return runFieldCalendarUrlSafe(
    "build",
    () => {
      const safe: FieldCalendarUrlState = {
        view: VIEWS.has(state.view) ? state.view : "month",
        selectedDay: coerceValidDay(state.selectedDay),
        anchorDate: coerceValidDay(state.anchorDate),
        mySchedule: Boolean(state.mySchedule),
        laneFilter:
          state.view === "day" && LANES.has(state.laneFilter) ? state.laneFilter : "all",
      };

      const params = new URLSearchParams();
      params.set("date", localDateInputValue(safe.selectedDay));
      params.set("view", safe.view);
      if (safe.view === "month" || safe.view === "week") {
        const a = coerceValidDay(safe.anchorDate);
        const ym = `${a.getFullYear()}-${String(a.getMonth() + 1).padStart(2, "0")}`;
        params.set("month", ym);
      }
      if (safe.mySchedule) params.set("mine", "1");
      if (safe.view === "day" && safe.laneFilter !== "all") {
        params.set("lane", safe.laneFilter);
      }
      const qs = params.toString();
      return qs ? `${FIELD_CALENDAR_PATH}?${qs}` : FIELD_CALENDAR_PATH;
    },
    FIELD_CALENDAR_FALLBACK_HREF,
    {
      selectedDay: state?.selectedDay,
      view: state?.view,
    },
  );
}

export function toUrlState(input: {
  viewMode: FieldCalendarViewMode;
  selectedDay: Date;
  anchorDate: Date;
  mySchedule: boolean;
  laneFilter: FieldVisitFilter;
}): FieldCalendarUrlState {
  return {
    view: VIEWS.has(input.viewMode) ? input.viewMode : "month",
    selectedDay: coerceValidDay(input.selectedDay),
    anchorDate: coerceValidDay(input.anchorDate),
    mySchedule: Boolean(input.mySchedule),
    laneFilter:
      input.viewMode === "day" && LANES.has(input.laneFilter) ? input.laneFilter : "all",
  };
}

export function fieldCalendarUrlStateEquals(
  a: FieldCalendarUrlState,
  b: FieldCalendarUrlState,
): boolean {
  try {
    return (
      a.view === b.view &&
      a.mySchedule === b.mySchedule &&
      a.laneFilter === b.laneFilter &&
      localDateInputValue(a.selectedDay) === localDateInputValue(b.selectedDay) &&
      localDateInputValue(a.anchorDate) === localDateInputValue(b.anchorDate)
    );
  } catch (error) {
    logFieldCalendarUrlCrash("stateEquals", error, {});
    return false;
  }
}

/** Canonical query string for shallow compare (ignores stale/unknown params). */
export function canonicalFieldCalendarQuery(searchStr: string): string {
  const href = buildFieldCalendarHref(parseFieldCalendarUrlSearch(searchStr));
  const idx = href.indexOf("?");
  return idx >= 0 ? href.slice(idx + 1) : "";
}

export function getEffectiveFieldCalendarSearch(searchStr: string): string {
  if (typeof searchStr === "string" && searchStr.length > 0) {
    return searchStr.startsWith("?") ? searchStr.slice(1) : searchStr;
  }
  if (typeof window !== "undefined" && window.location.pathname === FIELD_CALENDAR_PATH) {
    return window.location.search.replace(/^\?/, "");
  }
  return "";
}

/** Safe internal return path for SVR detail (`?returnTo=`). */
export function safeFieldCalendarReturnTo(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch (error) {
    logFieldCalendarUrlCrash("returnTo.decode", error, { returnTo: trimmed.slice(0, 120) });
    return null;
  }

  if (decoded.length > MAX_RETURN_TO_LEN) return null;
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
  if (/[\r\n]/.test(decoded)) return null;

  try {
    const url = new URL(decoded, "https://local.invalid");
    if (url.origin !== "https://local.invalid") return null;
    if (!url.pathname.startsWith("/")) return null;
  } catch {
    return null;
  }

  return decoded;
}

export function fieldCalendarReturnToOrFallback(raw: string | null | undefined): string {
  return safeFieldCalendarReturnTo(raw) ?? FIELD_CALENDAR_FALLBACK_HREF;
}

export function currentFieldCalendarHref(): string {
  if (typeof window === "undefined") return FIELD_CALENDAR_FALLBACK_HREF;
  if (window.location.pathname === FIELD_CALENDAR_PATH) {
    return `${window.location.pathname}${window.location.search}`;
  }
  return FIELD_CALENDAR_FALLBACK_HREF;
}

export function safeFieldCalendarNavigateHref(href: string | null | undefined): string {
  const safe = safeFieldCalendarReturnTo(href);
  if (safe?.startsWith(FIELD_CALENDAR_PATH)) return safe;
  if (safe) return safe;
  return FIELD_CALENDAR_FALLBACK_HREF;
}
